import { getApiBaseUrl } from './chatIdentity';

const API_BASE_URL = getApiBaseUrl();
const SHOP_AUTH_BASE_URL = `${API_BASE_URL}/shop-auth`;
const PARTNER_DASHBOARD_BASE_URL = `${API_BASE_URL}/api/partner-dashboard`;

function pickDetail(data, fallback) {
    const detail = data?.detail ?? data?.message ?? data?.error;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
        const first = detail.find((item) => typeof item?.msg === 'string');
        if (first) return first.msg;
    }
    if (detail && typeof detail === 'object') {
        return detail.message || detail.msg || detail.detail || fallback;
    }
    return fallback;
}

async function readShopAuthResponse(response, fallback) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const error = new Error(pickDetail(data, fallback));
        // Callers branch on these: 401 sends the reset flow back to the code step, and
        // 429 drives the resend countdown off the server's own Retry-After.
        error.status = response.status;
        error.data = data;
        const retryAfter = Number(response.headers.get('Retry-After'));
        if (Number.isFinite(retryAfter) && retryAfter > 0) {
            error.retryAfterSeconds = Math.ceil(retryAfter);
        }
        throw error;
    }
    return data;
}

export async function getShopAuthState() {
    const response = await fetch(`${SHOP_AUTH_BASE_URL}/me`, {
        credentials: 'include',
    });
    return readShopAuthResponse(response, 'Could not restore shop session.');
}

export async function loginShopAccount({ identifier, password }) {
    const response = await fetch(`${SHOP_AUTH_BASE_URL}/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password }),
    });
    return readShopAuthResponse(response, 'Could not sign in.');
}

export async function signupShopAccount(payload) {
    const response = await fetch(`${SHOP_AUTH_BASE_URL}/signup`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    return readShopAuthResponse(response, 'Could not create shop account.');
}

// Step 1 → 2. Catches an email or phone that already has an account before the owner
// fills in their whole shop profile. Sends no codes, so it costs nothing to call.
export async function checkShopSignupIdentifiers({ email, phone, signal }) {
    const response = await fetch(`${SHOP_AUTH_BASE_URL}/signup/check`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, phone }),
        signal,
    });
    return readShopAuthResponse(response, 'Could not check those details.');
}

// Step 2 → 3, and every resend. Omit channel to send both; pass 'email' or 'sms' to
// resend just one, so a resend does not burn the other channel's budget too.
export async function sendShopSignupCodes({ email, phone, channel }) {
    const response = await fetch(`${SHOP_AUTH_BASE_URL}/signup/send-codes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, phone, channel: channel || null }),
    });
    return readShopAuthResponse(response, 'Could not send the verification codes.');
}

// Per-field check as the owner types. Does not spend the code — the final submit does.
export async function verifyShopSignupCode({ identifier, channel, otp }) {
    const response = await fetch(`${SHOP_AUTH_BASE_URL}/signup/verify-code`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, channel, otp }),
    });
    return readShopAuthResponse(response, 'Could not verify that code.');
}

// Results for the "find your shop on Google" picker at signup. Photo URLs come back
// relative because they point at our own proxy, so they are absolutized here rather than
// in the component.
export async function lookupShopPlaces({ input, signal } = {}) {
    const response = await fetch(`${API_BASE_URL}/api/shops/place-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ input }),
        signal,
    });
    const data = await readShopAuthResponse(response, 'Could not search Google for that shop.');
    return (Array.isArray(data?.suggestions) ? data.suggestions : []).map((suggestion) => ({
        ...suggestion,
        photo_url: suggestion.photo_url ? `${API_BASE_URL}${suggestion.photo_url}` : '',
    }));
}

// City and state for a 5-digit US ZIP: { zip, state, cities } (a ZIP can have several
// accepted city names). 404 = no such ZIP; 503 = the lookup is unavailable.
export async function lookupUsZip(zip, { signal } = {}) {
    const response = await fetch(`${API_BASE_URL}/api/shops/zip-lookup?${new URLSearchParams({ zip })}`, { signal });
    return readShopAuthResponse(response, "We couldn't verify the ZIP code right now. Please try again in a moment.");
}

// Everything needed to fill the address fields from a chosen listing, plus a week of
// hours and a warning when Google's own hours had to be adjusted to be storable.
export async function fetchShopPlaceDetails({ placeId } = {}) {
    const params = new URLSearchParams({ place_id: placeId });
    const response = await fetch(`${API_BASE_URL}/api/shops/place-details?${params.toString()}`);
    const data = await readShopAuthResponse(response, 'Could not load that shop from Google.');
    return data?.place || null;
}

// ── Changing a contact detail from the settings page ──────────────────────────
// Every value here is the NEW one. The code goes to that new value, so holding it is
// what proves the owner controls it. Nothing is written until updateShopProfile below
// is called with the codes.
//
// These two are the shop's public details AND the credentials the owner signs in with —
// registration writes one value into both — so a confirmed change moves both together
// and the old value stops signing them in.

// Pass only the fields being changed. onlyField resends a single one, so a resend burns
// that target's budget rather than every channel's. A field whose value already matches
// what is stored comes back absent from `fields` and costs nothing.
export async function sendShopContactChangeCodes({
    shopContactEmail, shopPhone, onlyField,
} = {}) {
    const response = await fetch(`${SHOP_AUTH_BASE_URL}/profile/contact/send-codes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            shop_contact_email: shopContactEmail || null,
            shop_phone: shopPhone || null,
            only_field: onlyField || null,
        }),
    });
    return readShopAuthResponse(response, 'Could not send the confirmation codes.');
}

// Per-field check as the owner types. Does not spend the code — saving does.
export async function verifyShopContactChangeCode({ identifier, channel, otp }) {
    const response = await fetch(`${SHOP_AUTH_BASE_URL}/profile/contact/verify-code`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, channel, otp }),
    });
    return readShopAuthResponse(response, 'Could not verify that code.');
}

// Send only the fields that changed. Omitted keys are left untouched server-side,
// and the response is a fresh /me payload the caller can swap straight into state.
// A changed contact detail must travel with its code AND current_password, or the
// server refuses the write.
export async function updateShopProfile(changes) {
    const response = await fetch(`${SHOP_AUTH_BASE_URL}/profile`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changes),
    });
    return readShopAuthResponse(response, 'Could not save your changes.');
}

// ── Availability ──────────────────────────────────────────────────────────────
function partnerShopQuery({ partnerShopId } = {}) {
    if (!partnerShopId) return '';
    return `?${new URLSearchParams({ partner_shop_id: String(partnerShopId) }).toString()}`;
}

function appendPartnerShopQuery(url, options = {}) {
    return `${url}${partnerShopQuery(options)}`;
}

// Weekly hours, slot configuration and holiday answers. The shared dashboard route accepts
// either an admin cookie plus partnerShopId or a shop cookie scoped to its own shop.

export async function getShopAvailability(options = {}) {
    const response = await fetch(appendPartnerShopQuery(`${PARTNER_DASHBOARD_BASE_URL}/availability`, options), { credentials: 'include' });
    return readShopAuthResponse(response, 'Could not load your availability.');
}

// The whole week is replaced at once — the editor shows all seven days, so it sends
// all of them rather than trying to describe a diff.
export async function saveShopHours(windows, options = {}) {
    const response = await fetch(appendPartnerShopQuery(`${PARTNER_DASHBOARD_BASE_URL}/availability/hours`, options), {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ windows }),
    });
    return readShopAuthResponse(response, 'Could not save your opening hours.');
}

export async function saveShopSlotConfig(config, options = {}) {
    const response = await fetch(appendPartnerShopQuery(`${PARTNER_DASHBOARD_BASE_URL}/availability/config`, options), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
    });
    return readShopAuthResponse(response, 'Could not save your slot settings.');
}

// answer is 'open' (clears any override), 'closed', or 'custom' with times.
export async function saveShopSpecialHours(payload, options = {}) {
    const response = await fetch(appendPartnerShopQuery(`${PARTNER_DASHBOARD_BASE_URL}/availability/special-hours`, options), {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    return readShopAuthResponse(response, 'Could not save that date.');
}

export async function previewShopAvailability(days = 7, options = {}) {
    const params = new URLSearchParams({ days: String(days) });
    if (options.partnerShopId) params.set('partner_shop_id', String(options.partnerShopId));
    const response = await fetch(`${PARTNER_DASHBOARD_BASE_URL}/availability/preview?${params.toString()}`, {
        credentials: 'include',
    });
    return readShopAuthResponse(response, 'Could not preview your availability.');
}

export async function getReusableShopReferralLink(options = {}) {
    const params = new URLSearchParams();
    if (options.partnerShopId) params.set('partner_shop_id', String(options.partnerShopId));
    if (typeof window !== 'undefined' && window.location?.origin) params.set('app_origin', window.location.origin);
    const response = await fetch(`${PARTNER_DASHBOARD_BASE_URL}/referrals/general-link?${params.toString()}`, {
        credentials: 'include',
    });
    const data = await readShopAuthResponse(response, 'Could not load your reusable referral link.');
    return { url: data?.url || '', code: data?.code || '' };
}

// ── Time-off blocks (calendar "Block time off" / Upcoming closures) ─────────────
// Manual no-booking windows: a full day, or a start–end range on a day.

export async function getShopClosures(options = {}) {
    const response = await fetch(appendPartnerShopQuery(`${PARTNER_DASHBOARD_BASE_URL}/availability/closures`, options), { credentials: 'include' });
    const data = await readShopAuthResponse(response, 'Could not load your closures.');
    return Array.isArray(data?.closures) ? data.closures : [];
}

// payload: { name, on_date: 'YYYY-MM-DD', is_full_day, start_time?: 'HH:MM', end_time?: 'HH:MM', confirm_conflicts?: boolean }
export async function createShopClosure(payload, options = {}) {
    const response = await fetch(appendPartnerShopQuery(`${PARTNER_DASHBOARD_BASE_URL}/availability/closures`, options), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const data = await readShopAuthResponse(response, 'Could not block that time off.');
    return data?.closure || null;
}

export async function deleteShopClosure(closureId, options = {}) {
    const response = await fetch(appendPartnerShopQuery(`${PARTNER_DASHBOARD_BASE_URL}/availability/closures/${encodeURIComponent(closureId)}`, options), {
        method: 'DELETE',
        credentials: 'include',
    });
    return readShopAuthResponse(response, 'Could not remove that closure.');
}

// Password reset. The server returns the identifier it normalized and keyed the code
// on; every later step must send that exact string back, or the lookup misses.
export async function requestShopPasswordReset({ identifier }) {
    const response = await fetch(`${SHOP_AUTH_BASE_URL}/forgot-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
    });
    return readShopAuthResponse(response, 'Could not send the reset code.');
}

export async function verifyShopPasswordResetCode({ identifier, otp }) {
    const response = await fetch(`${SHOP_AUTH_BASE_URL}/verify-reset-code`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, otp }),
    });
    return readShopAuthResponse(response, 'Could not verify that code.');
}

export async function resetShopPassword({ identifier, otp, newPassword }) {
    const response = await fetch(`${SHOP_AUTH_BASE_URL}/reset-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, otp, new_password: newPassword }),
    });
    return readShopAuthResponse(response, 'Could not reset your password.');
}

export async function logoutShopAccount() {
    await fetch(`${SHOP_AUTH_BASE_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
    });
}
